import { Trans, useLingui } from '@lingui/react/macro';
import { Dialog, Button } from '@utima/ui';
import { Form, Input, type TypedFormState } from '@utima/ui-informed';
import { endOfDay, isAfter, startOfDay } from 'date-fns';
import { memo, useCallback, useState } from 'react';

import { useChatMutation } from '@/features/chats';

export interface ExportModalProps {
  isOpen: boolean;
  onCloseClick: (values: { isExporting: boolean }) => void;
}

type ExportFormValues = {
  from?: string;
  to?: string;
};

export const ExportModal = memo(function ExportModal({
  isOpen,
  onCloseClick,
}: ExportModalProps) {
  const { t } = useLingui();
  const [exportError, setExportError] = useState<string | undefined>(undefined);

  const [isExporting, setIsExporting] = useState(false);
  const { exportToXls } = useChatMutation();

  const onExportCloseClick = useCallback(() => {
    if (isExporting) return;
    setExportError(undefined);
    onCloseClick({ isExporting });
  }, [isExporting, onCloseClick]);

  // TODO: We should size-optimize this function in the future, possibly
  //       handle downloading in the background of this app (running promise)
  //       so user can continue using the application, etc
  const onExportRequest = async ({
    values,
  }: TypedFormState<ExportFormValues>) => {
    setExportError(undefined);
    if (values?.from && values?.to && isAfter(values.from, values.to)) {
      setExportError(t`"From" cannot be after "To" in the filter!`);

      return;
    }

    setIsExporting(true);
    try {
      const base64 = await exportToXls.mutateAsync({
        from: values?.from ? startOfDay(values.from).toISOString() : undefined,
        to: values?.to ? endOfDay(values.to).toISOString() : undefined,
      });

      const blob = new Blob(
        //@ts-expect-error there is an issue with ambiguous types
        [Uint8Array.from(atob(base64), char => char.codePointAt(0))],
        {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.setAttribute('href', url);
      link.setAttribute('target', '_self');
      link.setAttribute('download', 'kanbu-export.xlsx');

      // Append to html link element page
      document.body.append(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.remove();
      onCloseClick({ isExporting });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t`An unknown error occurred during export.`;

      setExportError(message);
    } finally {
      // Close the modal window
      setIsExporting(false);
      setExportError(undefined);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onExportCloseClick}>
      <Dialog.Content>
        <Form
          name='XlsxExportForm'
          onSubmit={onExportRequest}
          className='flex flex-col gap-4'
        >
          <Dialog.Header className='flex gap-5'>
            <Dialog.Title>
              <Trans>Export chats to XLSX</Trans>
            </Dialog.Title>
          </Dialog.Header>
          <div className='flex flex-col gap-2'>
            <div className='flex gap-2'>
              <Input label={t`From`} type='date' name='from' />
              <Input label={t`To`} type='date' name='to' />
            </div>
            {exportError && <p className='text-danger'>{exportError}</p>}
          </div>
          <Dialog.Footer className='mt-2'>
            <Button
              data-cta='admin-export-modal-cancel'
              variant='secondary'
              disabled={isExporting}
              onClick={onExportCloseClick}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              data-cta='admin-export-modal-export'
              disabled={isExporting}
              variant='primary'
              type='submit'
            >
              {isExporting ? (
                <Trans>Exporting...</Trans>
              ) : (
                <Trans>Export</Trans>
              )}
            </Button>
          </Dialog.Footer>
        </Form>
      </Dialog.Content>
    </Dialog.Root>
  );
});
